import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { ApiError } from 'utils/api';
import { logError } from 'utils/datadog';
import { getGuestForm, removeGuestFormFile, uploadGuestFormFile } from '../api';
import { isTerminalFormError } from '../formErrors';
import { useGuestFormSession } from '../GuestFormSessionContext';
import {
  signalMono,
  signalNote,
  signalSurface,
  signalTray,
  signalTraySegment,
  signalVisuallyHidden
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  acceptOf,
  describeTypes,
  formatMegabytes,
  imageTypesOf,
  mimeTypeOf,
  photoAcceptOf,
  readFileUploadAnswer,
  readFileUploadConfig,
  resolveUploadType
} from './fileUpload';
import { BlockRendererProps, questionIdOf } from './types';

/** Poll quickly while a scan normally finishes, then back off: a slow scan is not a reason to hammer. */
const POLL_FAST_MS = 2000;
const POLL_SLOW_MS = 10000;
const POLL_FAST_ATTEMPTS = 15;

/** A file as the server wants it: base64 with no `data:` prefix. */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });

const requiredIdOf = (block: { instanceId: string }) =>
  `form-file-required-${block.instanceId}`;

const KNOWN_ERRORS: Record<string, Parameters<typeof getTrans>[0]> = {
  'error.form-file-upload.scan-in-progress':
    'guestForm.fileUpload.error.scanInProgress',
  'error.form-file-upload.removal-in-progress':
    'guestForm.fileUpload.error.removalInProgress'
};

/**
 * `file_upload@1`: a PDF the respondent attaches, scanned for viruses before it counts.
 *
 * The answer here is never composed by this app. pro-core writes it — `PENDING_SCAN` the moment the
 * bytes land, `CLEAN` or `REJECTED` once the scan is done — and autosave ignores whatever this block
 * sends for it. So this component only calls the upload and remove endpoints and reports back what
 * the server answered. Nothing is pushed to the respondent, which is why a pending file is polled.
 *
 * The block reads the session itself instead of widening the renderer contract: it is the only type
 * that talks to the server directly, and a token passed to all twelve renderers would be a lie for
 * eleven of them.
 */
export const FileUploadBlock = ({
  block,
  answer,
  onChange,
  locale,
  onBusyChange,
  readOnly = false
}: BlockRendererProps) => {
  const { data, getSessionToken, recoverSession, routeError } =
    useGuestFormSession();
  const formRef = data?.formRef ?? '';
  const config = useMemo(() => readFileUploadConfig(block), [block]);
  const file = readFileUploadAnswer(answer);
  const typesText = describeTypes(config.allowedTypes, locale);
  const notAllowedText = getTransWithParams(
    'guestForm.fileUpload.error.notAllowed',
    { types: typesText }
  );

  const [busy, setBusy] = useState<'uploading' | 'removing' | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Tell the runner a request is in flight so it holds Next, Back and Submit (see `onBusyChange`).
  // Cleared on unmount as well: a lock that outlived this block would strand the whole form.
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  const isBusy = busy !== null;
  useEffect(() => {
    onBusyChangeRef.current?.(isBusy);
    return () => onBusyChangeRef.current?.(false);
  }, [isBusy]);

  // The block unmounts when the respondent navigates away, and the runner's `onChange` always speaks
  // for the block on screen. A late result delivered after unmount would be filed under whichever
  // question is showing by then, so nothing is reported once this is gone; the next mount re-reads
  // the server instead.
  const mountedRef = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const fileRef = useRef(file);
  fileRef.current = file;
  useEffect(() => {
    // Set on setup as well as cleared on cleanup: StrictMode runs setup, cleanup, setup in
    // development, and a flag only ever cleared would leave this block discarding every result.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** Run a call with the live token, re-redeeming the link once if the short session lapsed. */
  const withSession = useCallback(
    async <T,>(call: (token: string) => Promise<T>): Promise<T> => {
      const expired = () =>
        new ApiError({ code: 'error.guest.session-expired', values: {} });
      const retryWithFreshToken = async () => {
        const fresh = await recoverSession();
        if (!fresh) {
          throw expired();
        }
        return call(fresh);
      };
      const token = getSessionToken();
      if (!token) {
        return retryWithFreshToken();
      }
      try {
        return await call(token);
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.code === 'error.guest.session-expired'
        ) {
          return retryWithFreshToken();
        }
        throw error;
      }
    },
    [getSessionToken, recoverSession]
  );

  // Bumped when an upload or removal starts and again when it ends. A read remembers the value it
  // started under and is dropped if it moved: such a read describes the server from before the
  // change, and adopting it would undo the change on screen (a mount-time read that started with no
  // file would otherwise wipe the file an upload just reported, and stop the polling).
  const mutationsRef = useRef(0);

  /** Adopt the server's answer for this block, reporting only when it actually differs. */
  const refresh = useCallback(async () => {
    const startedUnder = mutationsRef.current;
    const form = await withSession(token => getGuestForm(formRef, token));
    if (!mountedRef.current || mutationsRef.current !== startedUnder) {
      return;
    }
    const next = form.answers?.[block.instanceId];
    const before = fileRef.current;
    const after = readFileUploadAnswer(next);
    if (
      before?.fileRefId !== after?.fileRefId ||
      before?.status !== after?.status
    ) {
      onChangeRef.current(next);
    }
  }, [withSession, formRef, block.instanceId]);

  const failure = useCallback(
    (error: unknown) => {
      if (isTerminalFormError(error)) {
        routeError(error, 'autosave');
        return;
      }
      if (!mountedRef.current) {
        return;
      }
      const code = error instanceof ApiError ? error.code : undefined;
      if (code === 'error.form-file-upload.too-large') {
        const ceiling = Number(
          error instanceof ApiError ? error.values?.ceiling : NaN
        );
        setErrorText(
          getTransWithParams('guestForm.fileUpload.error.tooLarge', {
            max: formatMegabytes(
              Number.isFinite(ceiling) && ceiling > 0
                ? ceiling
                : config.maxSizeBytes
            )
          })
        );
        return;
      }
      if (code === 'error.form-file-upload.mime-type.invalid') {
        setErrorText(notAllowedText);
        return;
      }
      if (code && KNOWN_ERRORS[code]) {
        setErrorText(getTrans(KNOWN_ERRORS[code]));
        return;
      }
      logError('Guest form file upload failed', error);
      setErrorText(getTrans('guestForm.fileUpload.error.generic'));
    },
    [routeError, config.maxSizeBytes, notAllowedText]
  );

  // Pick up a file the server already holds (an upload that finished after the respondent left this
  // question, or one already in the saved answers), once, when the block appears.
  useEffect(() => {
    // A locked question's saved file is already in hand and nothing here can change it, so there is nothing to pick up.
    if (!formRef || readOnly) {
      return;
    }
    refresh().catch(error => {
      if (isTerminalFormError(error)) {
        routeError(error, 'autosave');
      }
      // Otherwise stay quiet: the answer in hand is still the best guess, and polling retries.
    });
    // Mount only: re-running on every answer change would turn each save into another read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPending = file?.status === 'PENDING_SCAN';
  useEffect(() => {
    if (!isPending || !formRef || readOnly) {
      return undefined;
    }
    let attempts = 0;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      attempts += 1;
      try {
        await refresh();
      } catch (error) {
        if (isTerminalFormError(error)) {
          routeError(error, 'autosave');
          return;
        }
      }
      // A refresh that outlives the effect must not start the next round.
      if (!stopped) {
        schedule();
      }
    };
    const schedule = () => {
      timer = setTimeout(
        () => void poll(),
        attempts < POLL_FAST_ATTEMPTS ? POLL_FAST_MS : POLL_SLOW_MS
      );
    };
    schedule();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [isPending, formRef, readOnly, refresh, routeError]);

  const handlePick = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    // Reset so choosing the same file again (after a rejection, say) still fires `change`.
    event.target.value = '';
    if (!picked || busy) {
      return;
    }
    setErrorText(null);
    const pickedType = resolveUploadType(picked, config.allowedTypes);
    if (!pickedType) {
      setErrorText(notAllowedText);
      return;
    }
    if (picked.size > config.maxSizeBytes) {
      setErrorText(
        getTransWithParams('guestForm.fileUpload.error.tooLarge', {
          max: formatMegabytes(config.maxSizeBytes)
        })
      );
      return;
    }
    setBusy('uploading');
    mutationsRef.current += 1;
    try {
      const fileBytes = await toBase64(picked);
      const uploaded = await withSession(token =>
        uploadGuestFormFile(formRef, block.instanceId, token, {
          mimeType: mimeTypeOf(pickedType),
          fileBytes,
          originalFilename: picked.name
        })
      );
      if (mountedRef.current) {
        onChangeRef.current(uploaded);
      }
    } catch (error) {
      failure(error);
    } finally {
      mutationsRef.current += 1;
      if (mountedRef.current) {
        setBusy(null);
      }
    }
  };

  const handleRemove = async () => {
    if (busy) {
      return;
    }
    setErrorText(null);
    setBusy('removing');
    mutationsRef.current += 1;
    try {
      await withSession(token =>
        removeGuestFormFile(formRef, block.instanceId, token)
      );
      if (mountedRef.current) {
        onChangeRef.current(undefined);
      }
    } catch (error) {
      failure(error);
      // A failed removal leaves the answer REMOVING on the server; show it so the repeat is offered.
      await refresh().catch(() => undefined);
    } finally {
      mutationsRef.current += 1;
      if (mountedRef.current) {
        setBusy(null);
      }
    }
  };

  const pickFile = () => inputRef.current?.click();
  // One picker for every type. With documents and images both allowed, a phone's own sheet offers the
  // photo library, the camera and the file browser; an images-only block gets an extension-free
  // `accept`, since extensions make a phone fall back to its file browser.
  const imagesOnly =
    imageTypesOf(config.allowedTypes).length === config.allowedTypes.length;

  const status = file?.status;
  let body;
  if (busy === 'uploading') {
    body = (
      <Typography
        role="status"
        data-testid={`form-file-${block.instanceId}-uploading`}
      >
        {getTrans('guestForm.fileUpload.uploading')}
      </Typography>
    );
  } else if (status === 'PENDING_SCAN') {
    body = (
      <>
        <FileName name={file?.originalFilename} />
        <Typography role="status" sx={signalNote('neutral')}>
          {getTrans('guestForm.fileUpload.scanning')}
        </Typography>
      </>
    );
  } else if (status === 'CLEAN') {
    body = (
      <>
        <FileName name={file?.originalFilename} />
        <Typography sx={signalNote('positive')}>
          {getTrans('guestForm.fileUpload.clean')}
        </Typography>
      </>
    );
  } else if (status === 'REJECTED') {
    body = (
      <>
        <FileName name={file?.originalFilename} />
        <Typography role="alert" sx={signalNote('caution')}>
          {getTrans('guestForm.fileUpload.rejected')}
        </Typography>
      </>
    );
  } else if (status === 'SCAN_FAILED' || status === 'FILE_TOO_LARGE') {
    body = (
      <>
        <FileName name={file?.originalFilename} />
        <Typography role="alert" sx={signalNote('caution')}>
          {status === 'SCAN_FAILED'
            ? getTrans('guestForm.fileUpload.scanFailed')
            : getTransWithParams('guestForm.fileUpload.error.tooLarge', {
                max: formatMegabytes(config.maxSizeBytes)
              })}
        </Typography>
      </>
    );
  } else if (status === 'REMOVING') {
    body = (
      <>
        <FileName name={file?.originalFilename} />
        <Typography role="alert" sx={signalNote('caution')}>
          {getTrans('guestForm.fileUpload.removing')}
        </Typography>
      </>
    );
  } else {
    body = null;
  }

  /**
   * What the respondent can do next, as the segments of one tray: the main move is raised, the
   * rest sit flat. Nothing is offered mid-upload or while a scan runs (a pending file cannot be
   * replaced — the server refuses it — and it is rarely long enough to want cancelling).
   */
  const remove = {
    id: 'remove',
    label: getTrans('guestForm.fileUpload.remove'),
    onClick: () => void handleRemove()
  };
  let actions: Array<{
    id: string;
    label: string;
    onClick: () => void;
    primary?: boolean;
  }> = [];
  // A locked question offers nothing: its file is shown, and the actions would call the server directly, past the
  // runner's guard on `onChange` (the server refuses them too, as `error.form-response.block-locked`).
  if (busy !== 'uploading' && !readOnly) {
    if (status === 'CLEAN') {
      actions = [
        {
          id: 'replace',
          label: getTrans('guestForm.fileUpload.replace'),
          onClick: pickFile,
          primary: true
        },
        remove
      ];
    } else if (
      status === 'REJECTED' ||
      status === 'SCAN_FAILED' ||
      status === 'FILE_TOO_LARGE'
    ) {
      actions = [
        {
          id: 'choose',
          label: getTrans('guestForm.fileUpload.chooseAnother'),
          onClick: pickFile,
          primary: true
        },
        remove
      ];
    } else if (status === 'REMOVING') {
      actions = [
        {
          id: 'retry-remove',
          label: getTrans('guestForm.fileUpload.retryRemove'),
          onClick: () => void handleRemove(),
          primary: true
        }
      ];
    } else if (!status) {
      actions = [
        {
          id: 'choose',
          label: getTrans('guestForm.fileUpload.choose'),
          onClick: pickFile,
          primary: true
        }
      ];
    }
  }

  return (
    <Stack spacing={3}>
      <BlockHeading
        block={block}
        locale={locale}
        hint={getTransWithParams('guestForm.fileUpload.hint', {
          types: typesText,
          max: formatMegabytes(config.maxSizeBytes)
        })}
      />

      {/* One group for the whole control, always rendered. The status panel and the tray come and go
          with the file's state (nothing is offered mid-upload or mid-scan), so the question's
          label and its requirement hang off this wrapper rather than off either of them.
          `aria-required` is not allowed on a plain group, so the requirement is a described-by line,
          announced with the group as the other renderers' own required state is. */}
      <Stack
        spacing={3}
        role="group"
        aria-labelledby={questionIdOf(block)}
        aria-describedby={block.required ? requiredIdOf(block) : undefined}
      >
        {block.required && (
          <Box
            component="span"
            id={requiredIdOf(block)}
            sx={signalVisuallyHidden}
          >
            {getTrans('guestForm.fileUpload.required')}
          </Box>
        )}
        {body && (
          <Box sx={signalSurface}>
            <Stack spacing={1.5} alignItems="flex-start" sx={{ p: 2 }}>
              {body}
            </Stack>
          </Box>
        )}

        {actions.length > 0 && (
          <Box sx={signalTray}>
            {actions.map(action => (
              <ButtonBase
                key={action.id}
                disabled={!!busy}
                onClick={action.onClick}
                data-testid={`form-file-${block.instanceId}-${action.id}`}
                sx={signalTraySegment(!!action.primary)}
              >
                {action.label}
              </ButtonBase>
            ))}
          </Box>
        )}
      </Stack>

      {errorText && (
        <Typography
          role="alert"
          sx={signalNote('caution')}
          data-testid={`form-file-${block.instanceId}-error`}
        >
          {errorText}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={
          imagesOnly
            ? photoAcceptOf(config.allowedTypes)
            : acceptOf(config.allowedTypes)
        }
        hidden
        onChange={event => void handlePick(event)}
        data-testid={`form-file-${block.instanceId}-input`}
      />
    </Stack>
  );
};

const FileName = ({ name }: { name?: string | null }) =>
  name ? (
    <Typography sx={{ ...signalMono, wordBreak: 'break-all' }}>
      {name}
    </Typography>
  ) : null;
